import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../lib/api";
import ConversationHistoryModal from "./ConversationHistoryModal";
import OpsStatusDetailModal from "./OpsStatusDetailModal";
import ConversionDetailModal from "./ConversionDetailModal";
import {
  Calendar,
  Search,
  RefreshCw,
  Eye,
  MessageSquare,
  Users,
  UserCheck,
  Bot,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Sparkles,
  Download,
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  OPS_STATUS_OPTIONS,
  getOpsStatusBadgeClass,
  getOpsStatusDotColor,
} from "./opsStatusHelpers";
import {
  getUserStatusBadge,
  getConvertedBadge,
} from "./conversionHelpers";
import { exportConversationsToCSV } from "./exportHelpers";

// Format date to YYYY-MM-DD
function toISODate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function ConversationHistoryDashboard() {
  // Preset filter state: "all", "today", "last_7", "last_30" (default "last_30")
  const [activePreset, setActivePreset] = useState("last_30");

  // Date filters (defaults for last 30 days)
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toISODate(d);
  });
  const [toDate, setToDate] = useState(() => toISODate(new Date()));

  // Filters
  const [userType, setUserType] = useState("all");
  const [status, setStatus] = useState("all");
  const [opsStatus, setOpsStatus] = useState("all");
  const [isNew, setIsNew] = useState("all");
  const [isConverted, setIsConverted] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal inspection states & toasts
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [evaluationModalConv, setEvaluationModalConv] = useState(null);
  const [conversionModalConv, setConversionModalConv] = useState(null);
  const [dashboardToast, setDashboardToast] = useState(null);

  // Export CSV state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Data states
  const [stats, setStats] = useState({
    total_conversations: 0,
    total_messages: 0,
    active_conversations: 0,
    closed_conversations: 0,
    customer_conversations: 0,
    guest_conversations: 0,
  });
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    has_more: false,
  });

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle Preset selection
  const handlePreset = (presetKey) => {
    setActivePreset(presetKey);
    setPage(1);
    const today = new Date();
    const endStr = toISODate(today);

    if (presetKey === "all") {
      setFromDate("");
      setToDate("");
    } else if (presetKey === "today") {
      setFromDate(endStr);
      setToDate(endStr);
    } else if (presetKey === "last_7") {
      const past7 = new Date();
      past7.setDate(today.getDate() - 7);
      setFromDate(toISODate(past7));
      setToDate(endStr);
    } else if (presetKey === "last_30") {
      const past30 = new Date();
      past30.setDate(today.getDate() - 30);
      setFromDate(toISODate(past30));
      setToDate(endStr);
    }
  };

  // Fetch Dashboard Stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const res = await api.get("/ai/chat-history/stats", { params });
      if (res.data?.success && res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error("Failed to fetch chat history stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [fromDate, toDate]);

  // Fetch Conversations List
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
        sort: "latest",
      };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (userType && userType !== "all") params.user_type = userType;
      if (status && status !== "all") params.status = status;
      if (opsStatus && opsStatus !== "all") params.ops_status = opsStatus;
      // Note: is_new and is_converted are filtered client-side (Option 1) to eliminate backend subquery timeouts
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await api.get("/ai/chat-history/conversations", { params });
      if (res.data?.success && Array.isArray(res.data.conversations)) {
        setConversations(res.data.conversations);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      } else {
        setConversations([]);
      }
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
      setError(err?.response?.data?.detail || err?.message || "Failed to load conversations from server.");
    } finally {
      setLoading(false);
    }
  }, [page, perPage, fromDate, toDate, userType, status, opsStatus, debouncedSearch]);

  // Trigger fetches on filter change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Custom date picker handlers that clear active preset pill if manual date selected
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setActivePreset(null);
    setPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setActivePreset(null);
    setPage(1);
  };

  // Export all conversations matching current active filters as CSV
  const handleExportCSV = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportProgress("Preparing export...");
    try {
      const baseParams = {
        per_page: 100,
        sort: "latest",
      };
      if (fromDate) baseParams.from_date = fromDate;
      if (toDate) baseParams.to_date = toDate;
      if (userType && userType !== "all") baseParams.user_type = userType;
      if (status && status !== "all") baseParams.status = status;
      if (opsStatus && opsStatus !== "all") baseParams.ops_status = opsStatus;
      if (debouncedSearch) baseParams.search = debouncedSearch;

      let currentPage = 1;
      let lastPage = 1;
      let allRecords = [];

      do {
        setExportProgress(
          lastPage > 1
            ? `Fetching page ${currentPage} of ${lastPage}...`
            : "Fetching conversations..."
        );
        const res = await api.get("/ai/chat-history/conversations", {
          params: { ...baseParams, page: currentPage },
        });

        if (res.data?.success && Array.isArray(res.data.conversations)) {
          allRecords = allRecords.concat(res.data.conversations);
          if (res.data.pagination) {
            lastPage = res.data.pagination.last_page || 1;
          }
        } else {
          break;
        }

        currentPage++;
      } while (currentPage <= lastPage);

      // Apply client-side filters for is_new and is_converted
      if (isNew && isNew !== "all") {
        allRecords = allRecords.filter((c) => (c.is_new || "").toLowerCase() === isNew.toLowerCase());
      }
      if (isConverted && isConverted !== "all") {
        allRecords = allRecords.filter((c) => (c.is_converted || "").toLowerCase() === isConverted.toLowerCase());
      }

      if (allRecords.length === 0) {
        setDashboardToast("No conversations match your current filters to export.");
        setTimeout(() => setDashboardToast(null), 3500);
        return;
      }

      setExportProgress("Generating CSV...");
      const timestamp = toISODate(new Date());
      exportConversationsToCSV(allRecords, `spincycles_conversations_${timestamp}.csv`);

      setDashboardToast(`Export completed! Downloaded ${allRecords.length} conversations.`);
      setTimeout(() => setDashboardToast(null), 4000);
    } catch (err) {
      console.error("Export error:", err);
      setDashboardToast(
        err?.response?.data?.detail || err?.message || "Failed to export conversations. Please try again."
      );
      setTimeout(() => setDashboardToast(null), 4000);
    } finally {
      setIsExporting(false);
      setExportProgress("");
    }
  };

  // Client-side in-memory filtering for Customer Type (is_new) and Converted (is_converted) - Option 1
  const displayConversations = useMemo(() => {
    return conversations.filter((c) => {
      if (isNew && isNew !== "all") {
        const cNew = (c.is_new || "").toLowerCase();
        if (isNew.toLowerCase() !== cNew) return false;
      }
      if (isConverted && isConverted !== "all") {
        const cConv = (c.is_converted || "").toLowerCase();
        if (isConverted.toLowerCase() !== cConv) return false;
      }
      return true;
    });
  }, [conversations, isNew, isConverted]);

  return (
    <div className="space-y-6" data-testid="ai-conversation-history-dashboard">
      {/* Top Banner - Matches screenshot style */}
      <div className="bg-gradient-to-r from-[#4d1f46] via-[#5a2452] to-[#6E3667] text-white rounded-xl px-5 py-3.5 shadow-md flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <span className="font-heading font-bold text-lg tracking-tight">SpinCycles AI Dashboard</span>
          <span className="bg-white/15 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-white/20 tracking-wider uppercase">
            API LIVE DEMO
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-[#2d1129]/80 border border-emerald-500/40 text-emerald-400 text-xs px-3 py-1 rounded-full flex items-center gap-2 font-medium shadow-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Bearer Token Configured & Active</span>
          </div>
          <button
            onClick={() => {
              fetchStats();
              fetchConversations();
            }}
            title="Refresh data"
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors"
          >
            <RefreshCw className={cn("w-4 h-4", (loading || statsLoading) && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* KPI Cards Row (7 Stat Cards) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {/* Total Conversations */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            TOTAL CONVERSATIONS
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {statsLoading ? "—" : Number(stats.total_conversations || 0).toLocaleString("en-IN")}
          </div>
        </div>

        {/* Total Messages */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            TOTAL MESSAGES
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {statsLoading ? "—" : Number(stats.total_messages || 0).toLocaleString("en-IN")}
          </div>
        </div>

        {/* Customer Chats */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            CUSTOMER CHATS
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {statsLoading ? "—" : Number(stats.customer_conversations || 0).toLocaleString("en-IN")}
          </div>
        </div>

        {/* Guest Sessions */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            GUEST SESSIONS
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {statsLoading ? "—" : Number(stats.guest_conversations || 0).toLocaleString("en-IN")}
          </div>
        </div>

        {/* Active / Closed */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            ACTIVE / CLOSED
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {statsLoading
              ? "— / —"
              : `${stats.active_conversations || 0} / ${stats.closed_conversations || 0}`}
          </div>
        </div>

        {/* Converted Post-Chat */}
        <div className="bg-white border border-emerald-200 rounded-xl p-3.5 shadow-xs bg-emerald-50/20">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
            CONVERTED POST-CHAT
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-emerald-800 mt-1 flex items-baseline gap-1.5">
            {statsLoading ? "—" : stats.is_converted?.yes ?? 0}
            <span className="text-xs font-semibold text-slate-400 font-sans">
              / {statsLoading ? "—" : (stats.is_converted?.no ?? 0)}
            </span>
          </div>
        </div>

        {/* New / Returning Customer */}
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-xs col-span-2 sm:col-span-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            NEW / RETURNING
          </div>
          <div className="kpi-num text-xl sm:text-2xl font-extrabold text-slate-900 mt-1 flex items-baseline gap-1.5">
            {statsLoading
              ? "— / —"
              : `${stats.is_new?.new ?? 0} / ${stats.is_new?.old ?? 0}`}
          </div>
        </div>
      </div>

      {/* Filter Controls Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {/* Preset Buttons */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Date Range Presets
            </label>
            <div className="inline-flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 gap-0.5">
              {[
                { key: "all", label: "All Time" },
                { key: "today", label: "Today" },
                { key: "last_7", label: "Last 7 Days" },
                { key: "last_30", label: "Last 30 Days" },
              ].map((p) => {
                const isActive = activePreset === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => handlePreset(p.key)}
                    className={cn(
                      "px-3 py-1.5 text-xs font-semibold rounded-md transition-all",
                      isActive
                        ? "bg-[#6E3667] text-white shadow-xs"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* From Date */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              From Date
            </label>
            <div className="relative flex items-center">
              <input
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                className="w-38 pl-3 pr-2 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
              />
            </div>
          </div>

          {/* To Date */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              To Date
            </label>
            <div className="relative flex items-center">
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                className="w-38 pl-3 pr-2 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
              />
            </div>
          </div>

          {/* User Type */}
          <div className="space-y-1 min-w-[130px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              User Type
            </label>
            <select
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white cursor-pointer"
            >
              <option value="all">All Users</option>
              <option value="customer">Customer</option>
              <option value="guest">Guest</option>
            </select>
          </div>

          {/* Status */}
          <div className="space-y-1 min-w-[130px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Ops Status */}
          <div className="space-y-1 min-w-[150px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Ops Status
            </label>
            <select
              value={opsStatus}
              onChange={(e) => {
                setOpsStatus(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white cursor-pointer"
            >
              <option value="all">All Ops Statuses</option>
              <option value="Pending">🟡 Pending</option>
              <option value="Under review">🔵 Under review</option>
              <option value="Converted">🟢 Converted</option>
              <option value="valid response">🟢 valid response</option>
              <option value="Improve - KB - RD">🟠 Improve - KB - RD</option>
            </select>
          </div>

          {/* Customer Type (is_new) */}
          <div className="space-y-1 min-w-[130px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Customer Type
            </label>
            <select
              value={isNew}
              onChange={(e) => {
                setIsNew(e.target.value);
              }}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white cursor-pointer"
            >
              <option value="all">All Customer Types</option>
              <option value="new">New (0 Orders)</option>
              <option value="old">Old / Returning</option>
            </select>
          </div>

          {/* Conversion (is_converted) */}
          <div className="space-y-1 min-w-[130px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Converted
            </label>
            <select
              value={isConverted}
              onChange={(e) => {
                setIsConverted(e.target.value);
              }}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white cursor-pointer"
            >
              <option value="all">All Conversion</option>
              <option value="yes">Converted (Yes)</option>
              <option value="no">Not Converted (No)</option>
            </select>
          </div>

          {/* Search Query */}
          <div className="space-y-1 flex-1 min-w-[220px]">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
              Search Query
            </label>
            <div className="relative flex items-center">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search customer, title, message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 text-slate-400 hover:text-slate-600 p-0.5"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Export Button */}
          <div className="space-y-1 self-end">
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isExporting}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-semibold shadow-xs transition-all h-[34px]",
                isExporting
                  ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                  : "bg-white hover:bg-[#6E3667]/5 text-[#6E3667] border border-[#6E3667]/40 hover:border-[#6E3667] cursor-pointer hover:shadow-xs active:scale-98"
              )}
              title="Download all reports for the current filter selection as CSV"
            >
              {isExporting ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-[#6E3667]" />
                  <span>{exportProgress || "Exporting..."}</span>
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5 text-[#6E3667]" />
                  <span>Export</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Conversations Table Panel */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50/90 border-b border-slate-200 text-slate-500 font-semibold tracking-wide">
                <th className="py-3 px-4 w-16">Conv #</th>
                <th className="py-3 px-4 min-w-[160px]">User Details</th>
                <th className="py-3 px-4 min-w-[280px]">Conversation Title</th>
                <th className="py-3 px-4 w-28">User Type</th>
                <th className="py-3 px-4 w-24">Messages</th>
                <th className="py-3 px-4 w-24">Status</th>
                <th className="py-3 px-4 w-32">Customer Type</th>
                <th className="py-3 px-4 w-36">Ops Status</th>
                <th className="py-3 px-4 w-28">Converted</th>
                <th className="py-3 px-4 w-36">Last Active</th>
                <th className="py-3 px-4 w-28 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="py-16 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                      <span className="text-xs font-medium">Fetching conversations...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-red-500">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-md mx-auto">
                      <AlertCircle className="w-6 h-6 text-red-500" />
                      <span className="text-xs font-semibold">{error}</span>
                      <button
                        onClick={fetchConversations}
                        className="mt-1 px-3 py-1 bg-primary text-white rounded text-xs hover:bg-primary/90"
                      >
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : displayConversations.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-16 text-center text-slate-400">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-600">
                        {conversations.length > 0
                          ? "No conversations match your selected filters on this page"
                          : "No conversations found"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {conversations.length > 0
                          ? "Try changing your Customer Type or Conversion filter."
                          : "Try modifying your date range, search query, or filter options."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayConversations.map((c) => {
                  const isCust = c.user_type === "customer";
                  const isAct = c.status === "active";
                  const userStatusInfo = getUserStatusBadge(c.is_new);
                  const convertedBadgeInfo = getConvertedBadge(c.is_converted);
                  return (
                    <tr key={c.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Conv # */}
                      <td className="py-3.5 px-4 font-bold text-slate-900 whitespace-nowrap">
                        #{c.id}
                      </td>

                      {/* User Details */}
                      <td className="py-3.5 px-4">
                        {isCust && c.customer ? (
                          <div>
                            <div className="font-semibold text-slate-800">
                              {c.customer.name || `Customer #${c.customer.cuid}`}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {c.customer.mobile || c.customer.email || `ID: ${c.customer.cuid}`}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium text-slate-700">Guest User</div>
                            <div className="text-[10px] text-slate-400 font-mono mt-0.5 truncate max-w-[130px]" title={c.guest_session_id}>
                              {c.guest_session_id || "Anonymous"}
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Conversation Title & Last Message snippet */}
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-800 line-clamp-1">
                          {c.title || "Untitled Session"}
                        </div>
                        {c.last_message && (
                          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1 max-w-xl">
                            {c.last_message}
                          </div>
                        )}
                      </td>

                      {/* User Type */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border",
                            isCust
                              ? "bg-sky-50 text-sky-700 border-sky-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          )}
                        >
                          {isCust ? "Customer" : "Guest"}
                        </span>
                      </td>

                      {/* Messages Count */}
                      <td className="py-3.5 px-4 font-medium text-slate-600 whitespace-nowrap">
                        {c.message_count || 0} msgs
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border",
                            isAct
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-100 text-slate-600 border-slate-200"
                          )}
                        >
                          {isAct ? "Active" : "Closed"}
                        </span>
                      </td>

                      {/* Customer Type (is_new) */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border",
                            userStatusInfo.badgeClass
                          )}
                          title={userStatusInfo.description}
                        >
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              userStatusInfo.dotClass
                            )}
                          />
                          <span>{userStatusInfo.label}</span>
                        </span>
                      </td>

                      {/* Ops Status */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setEvaluationModalConv(c)}
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-transform hover:scale-105 cursor-pointer shadow-2xs",
                            getOpsStatusBadgeClass(c.ops_status || "Pending")
                          )}
                          title="Click to view or edit evaluation details"
                        >
                          <span
                            className={cn(
                              "w-2 h-2 rounded-full",
                              getOpsStatusDotColor(c.ops_status || "Pending")
                            )}
                          />
                          <span>{c.ops_status || "Pending"}</span>
                        </button>
                      </td>

                      {/* Converted (is_converted) */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setConversionModalConv(c)}
                          className={cn(
                            "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border transition-all cursor-pointer shadow-2xs hover:scale-105",
                            convertedBadgeInfo.badgeClass
                          )}
                          title="Click to view conversion details & history"
                        >
                          <span
                            className={cn(
                              "w-1.5 h-1.5 rounded-full",
                              convertedBadgeInfo.dotClass
                            )}
                          />
                          <span>{c.is_converted === "Yes" ? "Yes" : "No"}</span>
                          <ExternalLink className="w-2.5 h-2.5 opacity-60 ml-0.5" />
                        </button>
                      </td>

                      {/* Last Active */}
                      <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap text-[11px]">
                        {c.last_message_at || c.updated_at || c.created_at || "—"}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => setSelectedConversation(c)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#6E3667] hover:bg-[#5a2c54] text-white shadow-xs transition-colors cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>View History</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-5 py-3.5 bg-slate-50/70 border-t border-slate-200 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-600">
          <div className="flex items-center flex-wrap gap-2">
            <span>
              Page {pagination.current_page || 1} of {pagination.last_page || 1}{" "}
              <span className="text-slate-400">({pagination.total || 0} total conversations)</span>
            </span>
            {(isNew !== "all" || isConverted !== "all") && (
              <span className="px-2 py-0.5 rounded-full bg-[#6E3667]/10 text-[#6E3667] font-semibold text-[11px] border border-[#6E3667]/20">
                Showing {displayConversations.length} of {conversations.length} on this page
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className={cn(
                "px-3 py-1.5 rounded-md font-medium border border-slate-200 bg-white transition-colors",
                page <= 1 || loading
                  ? "opacity-40 cursor-not-allowed text-slate-400"
                  : "hover:bg-slate-100 text-slate-700"
              )}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page || 1, p + 1))}
              disabled={page >= (pagination.last_page || 1) || loading}
              className={cn(
                "px-3 py-1.5 rounded-md font-medium border border-slate-200 bg-white transition-colors",
                page >= (pagination.last_page || 1) || loading
                  ? "opacity-40 cursor-not-allowed text-slate-400"
                  : "hover:bg-slate-100 text-slate-700"
              )}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Full Transcript Inspection Modal */}
      {selectedConversation && (
        <ConversationHistoryModal
          conversationId={selectedConversation.id}
          initialData={selectedConversation}
          onClose={() => setSelectedConversation(null)}
          onEvaluationUpdated={(updatedEvaluation) => {
            setConversations((prev) =>
              prev.map((item) =>
                item.id === selectedConversation.id
                  ? {
                    ...item,
                    ops_status: updatedEvaluation.status,
                    evaluation: updatedEvaluation,
                  }
                  : item
              )
            );
            setSelectedConversation((prev) =>
              prev
                ? {
                  ...prev,
                  ops_status: updatedEvaluation.status,
                  evaluation: updatedEvaluation,
                }
                : prev
            );
            setDashboardToast(
              `Evaluation for #${selectedConversation.id} updated to "${updatedEvaluation.status}"`
            );
            setTimeout(() => setDashboardToast(null), 3500);
          }}
        />
      )}

      {/* Quick Ops Status Details & Edit Modal */}
      {evaluationModalConv && (
        <OpsStatusDetailModal
          conversation={evaluationModalConv}
          onClose={() => setEvaluationModalConv(null)}
          onSaved={(updatedEvaluation) => {
            const convId = evaluationModalConv.id;
            setConversations((prev) =>
              prev.map((item) =>
                item.id === convId
                  ? {
                    ...item,
                    ops_status: updatedEvaluation.status,
                    evaluation: updatedEvaluation,
                  }
                  : item
              )
            );
            setEvaluationModalConv(null);
            setDashboardToast(
              `Evaluation for #${convId} updated to "${updatedEvaluation.status}"`
            );
            setTimeout(() => setDashboardToast(null), 3500);
          }}
        />
      )}

      {/* Conversion Details Modal */}
      {conversionModalConv && (
        <ConversionDetailModal
          conversation={conversionModalConv}
          onClose={() => setConversionModalConv(null)}
        />
      )}

      {/* Visual Toast Notification */}
      {dashboardToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-2.5 text-xs border border-slate-700 animate-fade-in font-medium">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{dashboardToast}</span>
        </div>
      )}
    </div>
  );
}
